import React from 'react';
import {UserProfileInterface} from 'src/reducers/users';
import {ReactComponent as BillingIcon} from 'src/assets/icons/receipt.svg';
import {getHeaders} from 'src/utils/requests';
import {SWFT_DIGITAL_API_HOST} from 'src/constants';
import CountryData from '../../../lib/country-data/country-data-typed';
import {useNavigate} from 'react-router-dom';
import {toast, ToastContainer} from 'react-toastify';
import {SpinnerIcon} from 'src/components/icons';
import {BaseScreen, ScreenDivider, ScreenHeader} from '../Screen';
import {InvoiceDetail} from 'src/api/types/billing';
import {IndianStateData} from 'src/lib/state-master';
import {UserLoader} from 'src/components/UserLoader';
import {DataLoader} from 'src/components/DataLoader';
import {nFetch} from 'src/api/nFetch';
import {ErrorTags, EventNames, EventProperty, ScreenNames} from 'src/lib/analytics/events';
import Tracker from 'src/lib/analytics/tracker';

const InputStyle = {
    default: 'w-full px-3 py-2 mt-1 border border-solid border-gray-300 rounded-md',
    disabled: 'w-full px-3 py-2 mt-1 border border-solid border-gray-300 rounded-md bg-gray-100',
};

function Input(props: {
    id: string;
    label: string;
    value: string;
    disabled?: boolean;
    onChange: (value: string) => void;
    required?: boolean;
}) {
    return (
        <div className={'flex flex-col'}>
            <label
                htmlFor={props.id}
                className={'block border border-solid border-transparent text-sm font-medium text-gray-700'}>
                {props.label}
                {props.required && <span className={'text-red-500'}>*</span>}
            </label>
            <input
                key={props.id}
                id={props.id}
                type={'text'}
                disabled={props.disabled}
                className={props.disabled ? InputStyle.disabled : InputStyle.default}
                value={props.value}
                onChange={(event) => props.onChange(event.target.value)}
                required={props.required}
            />
        </div>
    );
}

function Select<T>(props: {
    id: string;
    label: string;
    value: string;
    disabled?: boolean;
    onChange: (value: string) => void;
    data: T[];
    keyFn: (row: T) => string;
    valueFn: (row: T) => string;
    textFn: (row: T) => string;
    required?: boolean;
}) {
    return (
        <div className={'flex flex-col'}>
            <label
                htmlFor={props.id}
                className={'block border border-solid border-transparent text-sm font-medium text-gray-700'}>
                {props.label}
                {props.required && <span className={'text-red-500'}>*</span>}
            </label>
            <select
                key={props.id}
                id={props.id}
                disabled={props.disabled}
                className={props.disabled ? InputStyle.disabled : InputStyle.default}
                value={props.value}
                onChange={(event) => props.onChange(event.target.value)}
                required={props.required}>
                <option key={''} value={''}>
                    Select {props.label}
                </option>
                {props.data.map((row) => {
                    return (
                        <option key={props.keyFn(row)} value={props.valueFn(row)}>
                            {props.textFn(row)}
                        </option>
                    );
                })}
            </select>
        </div>
    );
}

function Form(props: {user: UserProfileInterface; existing?: InvoiceDetail}) {
    const navigate = useNavigate();

    const {user, existing} = props;
    const [first_name, setFirstName] = React.useState(existing?.first_name || user.first_name || '');
    const [last_name, setLastName] = React.useState(existing?.last_name || user.last_name || '');
    const [legal_name, setLegalName] = React.useState(existing?.legal_name || user.currentOrganization.name || '');
    const [email, setEmail] = React.useState(existing?.email || user.email);
    const [address_line_1, setAddressLine1] = React.useState(existing?.address_line_1 || '');
    const [address_line_2, setAddressLine2] = React.useState(existing?.address_line_2 || '');
    const [address_line_3, setAddressLine3] = React.useState(existing?.address_line_3 || '');
    const [country, setCountry] = React.useState(existing?.country || '');
    const [state, setState] = React.useState(existing?.state || '');
    const [city, setCity] = React.useState(existing?.city || '');
    const [postal_code, setPostalCode] = React.useState(existing?.postal_code || '');
    const [gst_number, setGstNumber] = React.useState(existing?.gst_number || '');
    const [pan_number, setPanNumber] = React.useState(existing?.pan_number || '');
    const [tax_id, setTaxId] = React.useState(existing?.tax_id || '');
    const [loading, setLoading] = React.useState(false);

    async function saveBillingDetails(org_uid: string, payload: Omit<InvoiceDetail, 'updated_at'>) {
        const {BILLING_DETAILS_ADD, BILLING_DETAILS_UPDATE} = EventNames;
        const event = props.existing != undefined ? BILLING_DETAILS_UPDATE : BILLING_DETAILS_ADD;

        setLoading(true);
        const request: Omit<InvoiceDetail, 'updated_at'> & {
            organization: string;
        } = {...payload, organization: org_uid};
        let response: Response;
        try {
            response = await fetch(`${SWFT_DIGITAL_API_HOST}/billing/detail/${org_uid}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(request),
            });
            Tracker.trackEvent(event, {
                [EventProperty.Country]: request.country,
            });
        } catch (e: any) {
            setLoading(false);
            toast.error('Something went wrong while saving billing details. Please try again later.');
            Tracker.trackError(e, ErrorTags.BILLING_DETAILS_ERROR);
            return;
        }

        if (response.status === 201) {
            setLoading(false);
            toast.success('Billing details saved successfully.', {
                autoClose: 3000,
            });
            setTimeout(() => {
                navigate('/settings/billing');
            }, 3000);
        } else {
            setLoading(false);
            toast.error('Something went wrong while saving billing details. Please try again later.');
        }
    }

    return (
        <form id={'billing-details'} className={'flex flex-col gap-6 p-5'}>
            <Input id={'email'} label={'Email'} value={email} onChange={(value) => setEmail(value)} required />

            <div className={'flex flex-row items-center gap-4'}>
                <div className={'w-1/2'}>
                    <Input
                        id={'first_name'}
                        label={'First Name'}
                        value={first_name}
                        onChange={(value) => setFirstName(value)}
                        required
                    />
                </div>
                <div className={'w-1/2'}>
                    <Input
                        id={'last_name'}
                        label={'Last Name'}
                        value={last_name}
                        onChange={(value) => setLastName(value)}
                    />
                </div>
            </div>

            <Input
                id={'legal_name'}
                label={'Company Name'}
                value={legal_name}
                onChange={(value) => setLegalName(value)}
                required
            />

            <Input
                id='address_line_1'
                label={'Address Line 1'}
                value={address_line_1}
                onChange={(value) => setAddressLine1(value)}
                required
            />
            <Input
                id='address_line_2'
                label={'Address Line 2'}
                value={address_line_2}
                onChange={(value) => setAddressLine2(value)}
            />
            <Input
                id='address_line_3'
                label={'Address Line 3'}
                value={address_line_3}
                onChange={(value) => setAddressLine3(value)}
            />

            <div className={'flex flex-row items-center gap-4'}>
                <div className={'w-1/2'}>
                    <Select
                        id={'country'}
                        label={'Country'}
                        value={country}
                        onChange={(value) => {
                            if (value !== country) {
                                if (
                                    country != '' &&
                                    !window.confirm('Changing country will reset various parameters. Are you sure?')
                                ) {
                                    return;
                                }

                                setCountry(value);
                                setState('');
                                setCity('');
                                setPostalCode('');
                                setGstNumber('');
                                setPanNumber('');
                                setTaxId('');
                            }
                        }}
                        data={CountryData}
                        keyFn={(row) => row.iso2}
                        valueFn={(row) => row.iso2}
                        textFn={(row) => row.name}
                        required
                    />
                </div>
                <div className={'w-1/2'}>
                    {country == 'in' ? (
                        <Select
                            id={'state'}
                            label={'State'}
                            value={state}
                            onChange={(value) => setState(value)}
                            data={IndianStateData}
                            keyFn={(row) => row.code.toString()}
                            valueFn={(row) => row.code.toString()}
                            textFn={(row) => row.name}
                            required
                        />
                    ) : (
                        <Input
                            id={'state'}
                            label={'State / Province'}
                            value={state}
                            onChange={(value) => setState(value)}
                        />
                    )}
                </div>
            </div>

            <div className={'flex flex-row items-center gap-4'}>
                <div className={'w-1/2'}>
                    <Input id={'city'} label={'City'} value={city} onChange={(value) => setCity(value)} />
                </div>
                <div className={'w-1/2'}>
                    <Input
                        id={'postal_code'}
                        label={'Postal Code'}
                        value={postal_code}
                        onChange={(value) => setPostalCode(value.replace(/[^0-9]/g, ''))}
                        required
                    />
                </div>
            </div>

            {country != '' && (
                <>
                    {country == 'in' ? (
                        <>
                            <div className={'flex flex-row items-center gap-4'}>
                                <div className={'w-1/2'}>
                                    <Input
                                        id={'gst_number'}
                                        label={'GST Number'}
                                        value={gst_number}
                                        onChange={(value) => setGstNumber(value)}
                                    />
                                </div>
                                <div className={'w-1/2'}>
                                    <Input
                                        id={'pan_number'}
                                        label={'PAN Number'}
                                        value={pan_number}
                                        onChange={(value) => setPanNumber(value)}
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <Input id={'tax_id'} label={'Tax ID'} value={tax_id} onChange={(value) => setTaxId(value)} />
                    )}
                </>
            )}

            <div className={'flex flex-row items-center gap-4'}>
                <div className={'w-1/2'}>
                    <button
                        type={'button'}
                        className={'w-full rounded-md bg-gray-100 px-3 py-2 font-semibold text-gray-700'}
                        onClick={(evt) => {
                            evt.preventDefault();
                            navigate('/settings/invoices');
                        }}>
                        Cancel
                    </button>
                </div>
                <div className={'w-1/2'}>
                    <button
                        type={'submit'}
                        className={'w-full rounded-md bg-blue-500 px-3 py-2 font-semibold text-white'}
                        onClick={(evt) => {
                            evt.preventDefault();
                            const form = document.getElementById('billing-details') as HTMLFormElement;
                            const isValid = form.checkValidity();
                            form.reportValidity();
                            if (!isValid) {
                                return;
                            }
                            const org_uid = user.currentOrganization.uid;
                            const payload: Omit<InvoiceDetail, 'updated_at'> = {
                                first_name,
                                last_name,
                                legal_name,
                                email,
                                address_line_1,
                                address_line_2,
                                address_line_3,
                                country,
                                state,
                                city,
                                postal_code,
                                gst_number,
                                pan_number,
                                tax_id,
                            };
                            console.log('Saving billing details', payload);
                            saveBillingDetails(org_uid, payload);
                        }}>
                        <div className={'flex flex-row items-center justify-center'}>
                            {loading && <SpinnerIcon />}
                            Save
                        </div>
                    </button>
                </div>
            </div>
            <ToastContainer position={'bottom-right'} />
        </form>
    );
}

function BillingDetailsForm(props: {user: UserProfileInterface; existing?: InvoiceDetail}) {
    const {user, existing} = props;

    console.debug('rendering BillingDetailsForm..', 'existing', existing);
    return (
        <>
            <ScreenHeader icon={<BillingIcon className={'h-6 w-6 stroke-black'} />}>
                <div className={'flex flex-col'}>
                    <span className={'font-semibold'}>
                        {existing ? 'Update Billing Information' : 'Add Billing Information'}
                    </span>
                    {!!existing && (
                        <span className={'text-sm text-gray-500'}>
                            updated at {new Date(existing.updated_at).toLocaleString()}
                        </span>
                    )}
                </div>
            </ScreenHeader>
            <ScreenDivider />
            <Form key={'billing-details'} user={user} existing={existing} />
        </>
    );
}

export default function BillingDetails() {
    const {BILLING_DETAILS_SCREEN} = ScreenNames;

    return (
        <BaseScreen screenName={BILLING_DETAILS_SCREEN}>
            <UserLoader>
                {(user) => (
                    <DataLoader
                        loader={async () => {
                            const org_uid = user.currentOrganization.uid;
                            const result = await nFetch<InvoiceDetail>(`/billing/detail/${org_uid}`);
                            if (!!result.error) throw result.error;
                            return result.data;
                        }}>
                        {(data) => <BillingDetailsForm user={user} existing={data} />}
                    </DataLoader>
                )}
            </UserLoader>
        </BaseScreen>
    );
}
